import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { TrendingUp, AlertTriangle, Calendar, Activity, Target } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { formatDistanceToNow, addDays, format } from 'date-fns';

interface LedgerEvent {
  id: string;
  event_type: string;
  shares: number;
  created_at: string;
}

interface ForecastData {
  date: string;
  actual: number | null;
  projected: number | null;
  conservative: number | null;
  moderate: number | null;
  aggressive: number | null;
  authorizedCap: number;
}

interface ScenarioMetrics {
  name: string;
  multiplier: number;
  monthlyRate: number;
  daysToCapacity: number;
  projectedDate: Date | null;
  color: string;
}

interface ShareCapitalForecastProps {
  companyId: string;
  authorizedShares: number | null;
}

export function ShareCapitalForecast({ companyId, authorizedShares }: ShareCapitalForecastProps) {
  const [ledgerEvents, setLedgerEvents] = useState<LedgerEvent[]>([]);
  const [currentShares, setCurrentShares] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      // Fetch ledger events (issuances and adjustments)
      const { data: events, error: eventsError } = await supabase
        .from('ledger_events')
        .select('id, event_type, shares, created_at')
        .eq('company_id', companyId)
        .in('event_type', ['ISSUANCE', 'ADJUSTMENT'])
        .order('created_at', { ascending: true });

      if (eventsError) throw eventsError;

      setLedgerEvents(events || []);

      // Get current total shares
      const { data: summary } = await supabase
        .from('cap_table_summary')
        .select('total_shares')
        .eq('company_id', companyId);

      const total = summary?.reduce((sum, entry) => sum + (entry.total_shares || 0), 0) || 0;
      setCurrentShares(total);
    } catch (error) {
      console.error('Error loading forecast data:', error);
    } finally {
      setLoading(false);
    }
  };

  const forecast = useMemo(() => {
    if (!authorizedShares || ledgerEvents.length < 2) {
      return null;
    }

    // Calculate historical issuance rate
    const firstEvent = new Date(ledgerEvents[0].created_at);
    const lastEvent = new Date(ledgerEvents[ledgerEvents.length - 1].created_at);
    const daysSinceFirst = Math.max(
      1,
      (lastEvent.getTime() - firstEvent.getTime()) / (1000 * 60 * 60 * 24)
    );

    const totalIssued = ledgerEvents.reduce((sum, event) => sum + event.shares, 0);
    const baseDailyRate = totalIssued / daysSinceFirst;
    const baseMonthlyRate = baseDailyRate * 30;

    // Calculate remaining capacity
    const remaining = authorizedShares - currentShares;
    const utilizationPct = (currentShares / authorizedShares) * 100;

    // Define scenarios with different multipliers
    const scenarios: ScenarioMetrics[] = [
      {
        name: 'Conservative',
        multiplier: 0.5,
        monthlyRate: baseMonthlyRate * 0.5,
        daysToCapacity: remaining / (baseDailyRate * 0.5),
        projectedDate: null,
        color: 'hsl(var(--success))',
      },
      {
        name: 'Current Trend',
        multiplier: 1.0,
        monthlyRate: baseMonthlyRate,
        daysToCapacity: remaining / baseDailyRate,
        projectedDate: null,
        color: 'hsl(var(--warning))',
      },
      {
        name: 'Moderate Growth',
        multiplier: 1.5,
        monthlyRate: baseMonthlyRate * 1.5,
        daysToCapacity: remaining / (baseDailyRate * 1.5),
        projectedDate: null,
        color: 'hsl(var(--chart-2))',
      },
      {
        name: 'Aggressive',
        multiplier: 2.0,
        monthlyRate: baseMonthlyRate * 2.0,
        daysToCapacity: remaining / (baseDailyRate * 2.0),
        projectedDate: null,
        color: 'hsl(var(--destructive))',
      },
    ];

    // Set projected dates for each scenario
    scenarios.forEach((scenario) => {
      if (baseDailyRate > 0 && remaining > 0 && scenario.daysToCapacity < Infinity) {
        scenario.projectedDate = addDays(new Date(), scenario.daysToCapacity);
      }
    });

    // Generate chart data
    const chartData: ForecastData[] = [];
    const today = new Date();

    // Historical data (last 6 months or all data if less)
    const sixMonthsAgo = new Date(today.getTime() - 180 * 24 * 60 * 60 * 1000);
    let runningTotal = 0;

    ledgerEvents
      .filter((e) => new Date(e.created_at) >= sixMonthsAgo)
      .forEach((event) => {
        runningTotal += event.shares;
        chartData.push({
          date: format(new Date(event.created_at), 'MMM dd'),
          actual: runningTotal,
          projected: null,
          conservative: null,
          moderate: null,
          aggressive: null,
          authorizedCap: authorizedShares,
        });
      });

    // Add today's point
    chartData.push({
      date: format(today, 'MMM dd'),
      actual: currentShares,
      projected: currentShares,
      conservative: currentShares,
      moderate: currentShares,
      aggressive: currentShares,
      authorizedCap: authorizedShares,
    });

    // Projected data - generate for longest scenario
    const maxDaysToCapacity = Math.max(
      ...scenarios.map((s) => s.daysToCapacity).filter((d) => d < Infinity)
    );
    const projectionDays = Math.min(365, maxDaysToCapacity);
    const projectionPoints = 12;
    const daysPerPoint = projectionDays / projectionPoints;

    for (let i = 1; i <= projectionPoints; i++) {
      const days = daysPerPoint * i;
      const futureDate = addDays(today, days);

      const dataPoint: ForecastData = {
        date: format(futureDate, 'MMM dd'),
        actual: null,
        projected: Math.min(currentShares + baseDailyRate * days, authorizedShares),
        conservative: Math.min(currentShares + baseDailyRate * 0.5 * days, authorizedShares),
        moderate: Math.min(currentShares + baseDailyRate * 1.5 * days, authorizedShares),
        aggressive: Math.min(currentShares + baseDailyRate * 2.0 * days, authorizedShares),
        authorizedCap: authorizedShares,
      };

      chartData.push(dataPoint);

      // Stop if all scenarios have reached capacity
      if (
        dataPoint.conservative >= authorizedShares &&
        dataPoint.moderate >= authorizedShares &&
        dataPoint.aggressive >= authorizedShares
      ) {
        break;
      }
    }

    return {
      baseDailyRate,
      baseMonthlyRate,
      remaining,
      utilizationPct,
      scenarios,
      chartData,
      isNearCapacity: utilizationPct >= 80,
      isCritical: utilizationPct >= 90,
    };
  }, [ledgerEvents, currentShares, authorizedShares]);

  if (!authorizedShares) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Set an authorized share capital limit in the settings above to see capacity forecasting.
        </AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Share Capital Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!forecast || ledgerEvents.length < 2) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Share Capital Forecast
          </CardTitle>
          <CardDescription>Predictive analytics for share capacity planning</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Activity className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Insufficient data for forecasting</p>
            <p className="text-sm text-muted-foreground">
              At least 2 share issuances are needed to generate projections
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Share Capital Forecast
            </CardTitle>
            <CardDescription>
              AI-powered projections based on historical issuance patterns
            </CardDescription>
          </div>
          {forecast.isCritical && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" />
              Critical
            </Badge>
          )}
          {forecast.isNearCapacity && !forecast.isCritical && (
            <Badge variant="outline" className="gap-1 border-warning text-warning">
              <AlertTriangle className="h-3 w-3" />
              Near Capacity
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="comparison" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="comparison">Scenario Comparison</TabsTrigger>
            <TabsTrigger value="details">Scenario Details</TabsTrigger>
          </TabsList>

          <TabsContent value="comparison" className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Current Utilization</p>
                <p className="text-2xl font-bold">{forecast.utilizationPct.toFixed(1)}%</p>
                <p className="text-xs text-muted-foreground">
                  {currentShares.toLocaleString()} / {authorizedShares.toLocaleString()}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Remaining Capacity</p>
                <p className="text-2xl font-bold">{forecast.remaining.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">shares available</p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Base Issuance Rate</p>
                <p className="text-2xl font-bold">
                  {forecast.baseMonthlyRate.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-muted-foreground">shares per month</p>
              </div>
            </div>

            {/* Alerts */}
            {forecast.isCritical && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Critical:</strong> You've used {forecast.utilizationPct.toFixed(1)}% of
                  authorized capacity. Consider increasing authorized shares or reducing issuance
                  rate.
                </AlertDescription>
              </Alert>
            )}

            {forecast.isNearCapacity && !forecast.isCritical && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Warning:</strong> You're approaching capacity limits. Review the scenario
                  projections to plan your capacity management strategy.
                </AlertDescription>
              </Alert>
            )}

            {/* Chart */}
            <div className="h-96 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={forecast.chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                    formatter={(value: any) => [value?.toLocaleString(), '']}
                  />
                  <Legend />
                  <ReferenceLine
                    y={authorizedShares}
                    stroke="hsl(var(--destructive))"
                    strokeDasharray="5 5"
                    label={{ value: 'Authorized Cap', position: 'insideTopRight' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="actual"
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    name="Historical"
                    dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="conservative"
                    stroke="hsl(var(--success))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Conservative (0.5x)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="hsl(var(--warning))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Current Trend (1x)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="moderate"
                    stroke="hsl(var(--chart-2))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Moderate (1.5x)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="aggressive"
                    stroke="hsl(var(--destructive))"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Aggressive (2x)"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <p className="text-xs text-muted-foreground">
              Scenario modeling based on {ledgerEvents.length} historical issuance events. Each
              scenario applies a different multiplier to your current issuance rate.
            </p>
          </TabsContent>

          <TabsContent value="details" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {forecast.scenarios.map((scenario) => (
                <Card key={scenario.name}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Target className="h-4 w-4" style={{ color: scenario.color }} />
                        {scenario.name}
                      </CardTitle>
                      <Badge
                        variant="outline"
                        style={{ borderColor: scenario.color, color: scenario.color }}
                      >
                        {scenario.multiplier}x
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Issuance Rate</p>
                      <p className="text-xl font-bold">
                        {scenario.monthlyRate.toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })}
                        <span className="text-sm font-normal text-muted-foreground ml-1">
                          / month
                        </span>
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Capacity Reached
                      </p>
                      {scenario.projectedDate && scenario.daysToCapacity < 10000 ? (
                        <>
                          <p className="text-lg font-bold">
                            {formatDistanceToNow(scenario.projectedDate, { addSuffix: false })}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(scenario.projectedDate, 'MMM dd, yyyy')}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-lg font-bold">Not projected</p>
                          <p className="text-xs text-muted-foreground">Within forecast window</p>
                        </>
                      )}
                    </div>

                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground">
                        {scenario.multiplier < 1 && 'Slower growth, more runway before capacity'}
                        {scenario.multiplier === 1 && 'Maintains current issuance pace'}
                        {scenario.multiplier > 1 &&
                          scenario.multiplier < 2 &&
                          'Moderate acceleration in share issuances'}
                        {scenario.multiplier >= 2 && 'Rapid growth, requires capacity planning'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Alert>
              <Target className="h-4 w-4" />
              <AlertDescription>
                <strong>Scenario Planning:</strong> Use these projections to plan capacity
                increases, funding rounds, and option pool refreshes. Conservative scenarios are
                ideal for stable operations, while aggressive scenarios help model high-growth
                periods.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
